import React, { useState, useEffect, useRef } from "react";
import CommitOnBlurNumberInput from "../CommitOnBlurNumberInput";
import { ChromePicker } from "react-color";
import { FONT_OPTIONS, normalizeFontValue, preloadMappedSdfFontsForText } from "../../../../../../engine/modules/ui/labelFontOptions.js";

const LABEL_TRANSFORM_SCALE_INPUT = "__labelTransformScale";

const DEFAULT_TEXT_FONT_SIZE = 90;
const DEFAULT_STYLE_VALUES = {
  lineSpacing: 20,
};

const HIDDEN_STYLE_FIELDS = ["padding", "paddingHeight", "outlineWidth", "sdfOutlineWidth", "strokeWidth", "sdfStrokeWidth"];

function getFontSizeFromStyle(style = {}) {
  const explicitFontSize = Number(style.fontSize);
  if (!isNaN(explicitFontSize) && explicitFontSize > 0) {
    return explicitFontSize;
  }

  const fontSizeMatch = String(style.font || "").match(/(\d+(?:\.\d+)?)px/);
  const fontSize = fontSizeMatch ? Number(fontSizeMatch[1]) : NaN;
  return !isNaN(fontSize) && fontSize > 0 ? fontSize : DEFAULT_TEXT_FONT_SIZE;
}

function getSelectedFontValue(font) {
  const normalizedFont = normalizeFontValue(font);
  const exactMatch = FONT_OPTIONS.find((option) => normalizeFontValue(option.value) === normalizedFont);
  if (exactMatch) return exactMatch.value;

  const familyMatch = [...FONT_OPTIONS]
    .sort((a, b) => b.label.length - a.label.length)
    .find((option) => normalizedFont.includes(normalizeFontValue(option.label)));
  return familyMatch ? familyMatch.value : FONT_OPTIONS[0].value;
}

function getStyleValue(style = {}, item) {
  if (Object.prototype.hasOwnProperty.call(style, item)) {
    return style[item];
  }

  return DEFAULT_STYLE_VALUES[item] ?? "";
}

function StylePanel(props) {
  const [style, setStyle] = useState(props.style);
  const [tempScale, setTempScale] = useState(props.scale);
  const [activeInput, setActiveInput] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (props.style !== style) {
      setStyle(props.style);
    }
  }, [props.style]);

  useEffect(() => {
    setTempScale(props.scale);
  }, [props.scale]);

  const handleChange = (e) => {
    const newStyle = { ...style };
    newStyle[activeInput] = e.target.value;
    setStyle(newStyle);
  };

  const handleNumericChange = (key, value) => {
    if (isNaN(value)) return;
    const newStyle = { ...style, [key]: Number(value) };
    setStyle(newStyle);
    const inventumStyle = JSON.parse(JSON.stringify(newStyle));
    Inventum.labels.setLabelStyle(props.id, inventumStyle);
    props.getLabels();
  };

  const handleColorChange = (value) => {
    const newStyle = { ...style, [activeInput]: value };
    setStyle(newStyle);
    const inventumStyle = JSON.parse(JSON.stringify(newStyle));
    Inventum.labels.setLabelStyle(props.id, inventumStyle);
    props.getLabels();
  };

  const handleFontChange = (font) => {
    const newStyle = {
      ...style,
      font,
      fontSize: getFontSizeFromStyle(style),
    };
    setStyle(newStyle);
    const inventumStyle = JSON.parse(JSON.stringify(newStyle));
    Inventum.labels.setLabelStyle(props.id, inventumStyle);
    setActiveInput(null);
    props.getLabels();
  };

  const updateStyle = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const clonedStyle = JSON.parse(JSON.stringify(style));
    if (activeInput === "font") {
      const fontSizeMatch = String(clonedStyle.font || "").match(/(\d+(?:\.\d+)?)px/);
      if (fontSizeMatch) {
        clonedStyle.fontSize = Number(fontSizeMatch[1]);
      }
    }
    Inventum.labels.setLabelStyle(props.id, clonedStyle);
    setStyle(clonedStyle);
    setActiveInput(null);
    props.getLabels();
  };

  const updateLabelTransformScale = (value = tempScale) => {
    if (isNaN(Number(value))) {
      setTempScale(props.scale);
      return;
    }

    const nextScale = Number(value);
    setTempScale(nextScale);
    Inventum.labels.scale(props.id, nextScale);
    setActiveInput(null);
    props.getLabels();
  };

  return (
    <div>
      <div className="EditorDrawerItemHeader" onClick={() => setOpen(!open)}>
        <span className="PanelOpenStatus">{open ? "▾" : "▸"}</span>
        <span>Style</span>
      </div>
      {open && (
        <StyleDrawer
          exclude={props.exclude}
          style={style}
          activeInput={activeInput}
          clearActiveInput={() => setActiveInput(null)}
          handleChange={handleChange}
          handleColorChange={handleColorChange}
          handleFontChange={handleFontChange}
          handleNumericChange={handleNumericChange}
          handleToggle={(key) => {
            setActiveInput((current) => current === key ? null : key);
          }}
          updateStyle={updateStyle}
          tempScale={tempScale}
          scale={props.scale}
          text={props.text}
          richText={props.richText}
          id={props.id}
          getLabels={props.getLabels}
          showLabelTransformScale={props.showLabelTransformScale}
          updateLabelTransformScale={updateLabelTransformScale}
        />
      )}
    </div>
  );
}

function StyleDrawer(props) {
  const excludeList = props.exclude || [];
  const fieldInputTypes = {
    color: ["color", "backgroundColor"],
    numeric: ["lineSpacing", "borderRadius", "fontSize"],
    text: ["font"],
  };

  // Organize fields into sections
  const colorFields = [];
  const spacingFields = [];
  const textFields = [];
  const unknownFields = [];
  
  const visibleStyleKeys = Object.keys(props.style)
    .filter((key) => !excludeList.includes(key) && !HIDDEN_STYLE_FIELDS.includes(key));

  if (!excludeList.includes("lineSpacing") && !visibleStyleKeys.includes("lineSpacing")) {
    visibleStyleKeys.push("lineSpacing");
  }
  visibleStyleKeys.forEach((key) => {
    if (fieldInputTypes.color.includes(key)) {
      colorFields.push(key);
    } else if (fieldInputTypes.numeric.includes(key)) {
      spacingFields.push(key);
    } else if (fieldInputTypes.text.includes(key)) {
      textFields.push(key);
    } else {
      // Fallback: treat unknown properties as text inputs
      unknownFields.push(key);
    }
  });

  return (
    <div className="StyleDrawerContainer">
      {props.showLabelTransformScale && Object.prototype.hasOwnProperty.call(props, "scale") && (
        <div className="StyleSection">
          <div className="StyleSectionTitle">
            <span className="material-icons">open_with</span>
            Transform
          </div>
          <div className="StyleSectionContent StyleGrid">
            <TransformScaleInput {...props} />
          </div>
        </div>
      )}
      
      {colorFields.length > 0 && (
        <div className="StyleSection StyleColorSection">
          <div className="StyleSectionTitle">
            <span className="material-icons">palette</span>
            Colors
          </div>
          <div className="StyleSectionContent StyleGrid StyleColorGrid">
            {colorFields.map((item, index) => (
              <ColorInput key={index} item={item} index={index} {...props} />
            ))}
          </div>
        </div>
      )}
      
      {spacingFields.length > 0 && (
        <div className="StyleSection">
          <div className="StyleSectionTitle">
            <span className="material-icons">space_bar</span>
            Spacing
          </div>
          <div className="StyleSectionContent StyleGrid">
            {spacingFields.map((item, index) => (
              <NumericInput key={index} item={item} index={index} {...props} />
            ))}
          </div>
        </div>
      )}
      
      {textFields.length > 0 && (
        <div className="StyleSection">
          <div className="StyleSectionTitle">
            <span className="material-icons">text_fields</span>
            Typography
          </div>
          <div className="StyleSectionContent">
            {textFields.map((item, index) => (
              item === "font" ? (
                <FontSelectInput key={index} item={item} index={index} {...props} />
              ) : (
                <TextInput key={index} item={item} index={index} {...props} />
              )
            ))}
          </div>
        </div>
      )}

      {unknownFields.length > 0 && (
        <div className="StyleSection">
          <div className="StyleSectionTitle">
            <span className="material-icons">settings</span>
            Other
          </div>
          <div className="StyleSectionContent">
            {unknownFields.map((item, index) => (
              <TextInput key={index} item={item} index={index} {...props} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const LABEL_MAP = {
  color: "Text Colour",
  backgroundColor: "Background Colour",
  padding: "Padding",
  lineSpacing: "Line Spacing",
  paddingHeight: "Padding Height",
  borderRadius: "Border Radius",
  font: "Font",
  fontSize: "Text Size",
};

// Define reasonable min/max values for numeric inputs
const NUMERIC_RANGES = {
  padding: { min: 0, max: 100 },
  lineSpacing: { min: 0, max: 50 },
  paddingHeight: { min: 0, max: 100 },
  borderRadius: { min: 0, max: 50 },
  fontSize: { min: 8, max: 300 },
};

function TextInput(props) {
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      props.updateStyle();
    }
  };

  return (
    <div className="StyleInputRow">
      <label className="StyleInputLabel" title={props.item}>
        {LABEL_MAP[props.item] ?? props.item}
      </label>
      {props.activeInput === props.item ? (
        <div className="StyleInputGroup">
          <input 
            className="StyleTextInput"
            onChange={props.handleChange} 
            value={getStyleValue(props.style, props.item)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleKeyDown}
          />
          <button className="StyleApplyButton" onClick={props.updateStyle}>
            <span className="material-icons">check</span>
          </button>
        </div>
      ) : (
        <div 
          className="StyleInputDisplay" 
          onClick={() => props.handleToggle(props.item)}
          title="Click to edit"
        >
          {props.style[props.item]}
        </div>
      )}
    </div>
  );
}

function FontSelectInput(props) {
  const selectedFontValue = getSelectedFontValue(props.style[props.item]);

  useEffect(() => {
    preloadMappedSdfFontsForText({ text: props.text, richText: props.richText });
  }, [props.text, props.richText]);

  return (
    <div className="MarkerInputRow StyleFontInputRow">
      <label className="MarkerInputLabel" title={props.item}>
        {LABEL_MAP[props.item] ?? props.item}
      </label>
      <div className="MarkerInputGroup">
        <select
          className="StyleSelectInput"
          value={selectedFontValue}
          onChange={(e) => props.handleFontChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        >
          {FONT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function NumericInput(props) {
  const range = NUMERIC_RANGES[props.item] || { min: 0, max: 100 };
  const displayName = LABEL_MAP[props.item] ?? props.item;

  const handleCommit = (value) => {
    props.handleNumericChange(props.item, value);
  };

  return (
    <div className="MarkerInputRow">
      <label className="MarkerInputLabel">{displayName}</label>
      <div className="MarkerInputGroup">
        <CommitOnBlurNumberInput
          className="MarkerNumberInput"
          wrapperClassName="InventumNumberInputControlFill"
          min={range.min}
          max={range.max}
          value={getStyleValue(props.style, props.item)}
          onCommit={handleCommit}
          onClick={(e) => e.stopPropagation()}
          title={`${range.min} - ${range.max}`}
        />
      </div>
    </div>
  );
}

function TransformScaleInput(props) {
  const handleCommit = (value) => {
    props.updateLabelTransformScale(value);
  };

  return (
    <div className="MarkerInputRow">
      <label className="MarkerInputLabel">Scale</label>
      <div className="MarkerInputGroup">
        <CommitOnBlurNumberInput
          className="MarkerNumberInput"
          wrapperClassName="InventumNumberInputControlFill"
          value={props.tempScale}
          onCommit={handleCommit}
          onClick={(e) => e.stopPropagation()}
          title="Label transform scale"
        />
      </div>
    </div>
  );
}

function ColorInput(props) {
  const isOpen = props.activeInput === props.item;

  return (
    <div className={`StyleInputRow StyleColorInputRow${isOpen ? " is-open" : ""}`}>
      <label className="StyleInputLabel" title={props.item}>
        {LABEL_MAP[props.item] ?? props.item}
      </label>
      <div className="StyleColorWrapper">
        <div
          className="ModelPropsSwatch StyleColorSwatch"
          tabIndex={0}
          onClick={() => props.handleToggle(props.item)}
          title="Click to change color"
        >
          <SwatchColor color={props.style[props.item]} />
          <span className="ModelPropsSwatchHex">{props.style[props.item]}</span>
        </div>
      </div>
      {isOpen ? (
        <ColorSelector
          handleClose={props.clearActiveInput}
          color={props.style[props.item]}
          handleChange={props.handleColorChange}
        />
      ) : null}
    </div>
  );
}

function SwatchColor(props) {
  const colorRef = useRef(null);

  useEffect(() => {
    if (colorRef.current) {
      colorRef.current.style.backgroundColor = props.color;
    }
  }, [props.color]);

  return <div className="ModelPropsSwatchColor" ref={colorRef} />;
}

function ColorSelector(props) {
  return (
    <div className="ModelPropsInlineColorPanel StyleColorPicker" onClick={(e) => { e.stopPropagation(); }}>
      <div className="ModelPropsColorPickerWrap">
        <ChromePicker
          onChange={(e) => {
            props.handleChange(`rgba(${e.rgb.r},${e.rgb.g},${e.rgb.b},${e.rgb.a})`);
          }}
          color={props.color}
        />
        <button className="StyleColorCloseButton" onClick={props.handleClose}>
          <span className="material-icons">close</span>
          Close
        </button>
      </div>
    </div>
  );
}

export {
  StylePanel,
  FONT_OPTIONS,
  getSelectedFontValue,
  getFontSizeFromStyle,
};
